import { inject, Injectable, InjectionToken } from '@angular/core';
import {
    BehaviorSubject,
    combineLatest,
    EMPTY,
    fromEvent,
    interval,
    merge,
    Observable,
    of,
    shareReplay,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { debounceTime, filter, map, startWith, tap } from 'rxjs/operators';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { environment } from '@environments/environment';
import { EntData } from '@bazis/shared/models/srv.types';
import { isLocal } from '@bazis/utils';

export const WS_PING_PONG_SUPPORT = new InjectionToken<boolean>('WS_PING_PONG_SUPPORT');

interface SocketConnection {
    message$: Observable<any>;
    opened$: Observable<any>;
    error$: Observable<any>;
    closed$: Observable<any>;
}

const WS_URL = environment.wsUrl;

@Injectable({ providedIn: 'root' })
export class BazisWebSocketService {
    private authService = inject(BazisAuthService);

    private enablePingPong = !!inject(WS_PING_PONG_SUPPORT, { optional: true });

    private socket;

    private retry$ = new BehaviorSubject(true);

    private pongReceived$ = new BehaviorSubject<boolean>(true);

    socketData$: Observable<SocketConnection> = combineLatest([
        this.retry$,
        this.authService.token.$,
    ]).pipe(
        debounceTime(0),
        map(([retry, token]) => {
            this.close();
            if (token) {
                const socket: SocketConnection = this.create();
                return { ...socket };
            }
            return {
                message$: of(''),
                opened$: of(null),
                error$: of(null),
                closed$: of(null),
            };
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    socketConnection$ = this.socketData$.pipe(
        switchMap((data) => merge(data.closed$, data.opened$, data.error$)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    private parsedMessage$ = this.socketData$.pipe(
        switchMap((v) => v.message$),
        filter(Boolean),
        map((message: any) => {
            try {
                return JSON.parse(message.data);
            } catch (e) {
                return message.data;
            }
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    private pingPongListener$ = this.parsedMessage$.pipe(
        tap((message: any) => {
            if (!this.enablePingPong) return;
            if (message.type === 'ping') {
                this.sendMessage(JSON.stringify({ type: 'pong', timestamp: new Date().getTime() }));
            } else if (message.type === 'pong') {
                this.pongReceived$.next(true);
            }
        }),
    );

    private pingPongHandler$ = this.socketData$.pipe(
        switchMap((socket) =>
            socket.opened$.pipe(
                switchMap(() => interval(5000).pipe(takeUntil(socket.closed$))),
                withLatestFrom(this.pongReceived$),
                tap(([_, pongReceived]) => {
                    if (!pongReceived) {
                        console.log('[SOCKET] Pong not received for last ping. Reconnecting.');
                        this.close();
                        this.retry$.next(true);
                        return;
                    }
                    // console.log('[SOCKET] Sending ping');
                    this.pongReceived$.next(false);
                    this.sendMessage(JSON.stringify({ type: 'ping' }));
                }),
            ),
        ),
    );

    private _messageReceiver$ = this.parsedMessage$.pipe(
        filter((message: any) => {
            if (!this.enablePingPong) return true;
            return message.type !== 'ping' && message.type !== 'pong';
        }),
        map((message: any): EntData => {
            const data = JSON.parse(message.data);

            if (data.task_id) {
                return {
                    id: data.task_id,
                    type: 'notification.async_task',
                    $snapshot: {
                        ...data,
                    },
                };
            }
            return {
                id: data.id,
                type: 'notification.notice',
                $snapshot: {
                    ...data,
                },
            };
        }),
        tap((data) => {
            console.log('[SOCKET MESSAGE]', data);
        }),
        startWith(null),
    );

    message$ = this.socketData$.pipe(
        switchMap(() => this._messageReceiver$),
        filter(Boolean),
    );

    constructor() {
        this.socketConnection$.subscribe();
        if (this.enablePingPong) {
            this.pingPongHandler$.subscribe();
            this.pingPongListener$.subscribe();
        }
    }

    close() {
        if (this.socket) {
            console.log('front closes connection');
            this.socket.close();
            this.socket = null;
        }
    }

    create(): SocketConnection {
        let url = WS_URL;
        if (!isLocal()) {
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
            url = `${protocol}://${window.location.host}/ws`;
        }

        this.socket = new WebSocket(url);

        const opened$ = fromEvent(this.socket, 'open').pipe(
            withLatestFrom(this.authService.token.$),
            tap(([connect, token]) => {
                console.log('Socket connected');
                this.pongReceived$.next(true);
                this.sendMessage(JSON.stringify({ token }));
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        const error$ = fromEvent(this.socket, 'error').pipe(
            tap((error) => {
                console.log('Socket connection error', error);
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        const closed$ = fromEvent(this.socket, 'close').pipe(
            tap(() => {
                console.log('Socket closed');
                if (this.socket) {
                    this.socket = null;
                }
            }),
            debounceTime(5000),
            tap(() => {
                this.retry$.next(true);
            }),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        const message$ = merge(fromEvent(this.socket, 'message')).pipe(
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        return { opened$, error$, closed$, message$ };
    }

    sendMessage(message: string) {
        if (!this.socket) return;
        this.socket.send(message);
    }
}
